import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import BorgStyleButton from "./BorgStyleButton"
import BorgStyleTextField from "./BorgStyleTextField";
import PasswordResetForm from "./PasswordResetForm";
import { Roles } from "src/Constants/BorgEnums";
import { loginUser, getUserPasswordResetPending, getUser, getOrganization, requestPasswordReset, checkPasswordResetAvailable } from "../Services/BorgAPIClient";
import { handleLogout } from '../Components/LogoutUtils';

interface LoginProps {
  onNeedResetPassword: () => void;
  onResetModeChange?: (isResetMode: boolean) => void;
}

const isStandAlone = process.env.REACT_APP_IS_STANDALONE === "true";
const productName = process.env.REACT_APP_PRODUCT_NAME ? process.env.REACT_APP_PRODUCT_NAME : "borg"
console.log(`IS STANDALONE=${isStandAlone} PRODUCT NAME=${productName}`)

const Login: React.FC<LoginProps> = ({ onNeedResetPassword, onResetModeChange }) => {
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState({} as any);
  const navigate = useNavigate();
  const cookies = new Cookies();
  const [isBuyOpen, setIsBuyOpen] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [isPasswordResetAvailable, setIsPasswordResetAvailable] = useState(false);

  useEffect(() => {
    const checkPasswordResetConfig = async () => {
      try {
        const configResult = await checkPasswordResetAvailable();
        setIsPasswordResetAvailable(configResult.isEmailConfigured);

        if (!configResult.isEmailConfigured) {
          console.log('Email password reset is not configured. "Forgot password?" option will be hidden.');
        } else {
          console.log('Email password reset is available.');
        }
      } catch (error) {
        console.log('Could not check password reset configuration. Assuming email reset is not available.', error);
        setIsPasswordResetAvailable(false);
      }
    };

    checkPasswordResetConfig();
  }, []);

  const handlePasswordReset = async () => {
    if (isResetLoading) return; // Prevent multiple submissions
    
    if (!resetEmail) {
      setResetError("Please enter your email.");
      return;
    }

    setIsResetLoading(true);
    setResetError("");
    setResetMessage("");

    try {
      const response = await requestPasswordReset(resetEmail);
      setResetMessage("If your email is in the system, a reset link has been sent. If you do not see an email, please check your spam folder.");
      setResetError("");
    } catch (e) {
      setResetError("Something went wrong. Please try again.");
    } finally {
      setIsResetLoading(false);
    }
  };

  const passwordResetMsg = "If you forgot your password, please contact your administrator to have it reset.";

  const submitLogin = async () => {
    let mustReset;

    try {
      mustReset = await getUserPasswordResetPending(username);
      if (mustReset) {
        onNeedResetPassword(); // Inform the parent component that reset is needed
        return;
      }
      setPassword("");
    } catch (err: any) {
      // will return NOT FOUND if no password reset is pending
    }

    if (!mustReset) {
      try {
        let userId = await loginUser({
          username: username,
          password: password,
        });

        cookies.set("username", username);
        cookies.set("userId", userId);

        const canPlayStatus = JSON.parse(localStorage.getItem('canPlay') as string);
        if (userId === null) {
          const msg = "No logged in user. If this problem persists, please contact your administrator."
          console.log(msg);
          setError(msg);
          setFieldErrors(null);
          await handleLogout();
          return;
        } else if (!canPlayStatus) {
          let msg = "User or organization account is disabled. Please contact your administrator.";
          try {
            const userRes = await getUser(userId.replace(/"|'/g, ''));
            const user = userRes?.data;
            if (user?.organizationId) {
              const orgRes = await getOrganization(user.organizationId);
              const org = orgRes?.data;
              const trialEnded = org && !org.isPaid && (!org.trialEndDate || new Date(org.trialEndDate) <= new Date());
              if (trialEnded) {
                const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'support@auria.space';
                msg = `Your organization's trial has expired. Please reach out to ${supportEmail} for assistance.`;
              }
            }
          } catch {
          }
          setError(msg);
          setFieldErrors(null);
          await handleLogout();
          return;
        }

        //Obtain logged in user profile information inorder to identify role
        let loggedInUser = await getUser(userId.replace(/"|'/g, ''));
        loggedInUser = loggedInUser.data;

        //If logged in user is of an admin distinction then navigate to admin panel. If instructor go to instructor page. Otherwise route to play
        if (loggedInUser.roles.length > 0 &&
          (loggedInUser.roles[0] === Roles.SystemAdmin || loggedInUser.roles[0] === Roles.OrganizationAdmin)) {
          navigate("/admin/users");
        }
        else if (loggedInUser.roles.length > 0 &&
          loggedInUser.roles[0] === Roles.Instructor) {
          navigate("/instructor");
        }
        else {
          navigate("/play");
        }

      } catch (err: any) {
        if (err && err.errors) {
          let errMsg = `Login failed: ${err.title}`;
          if (err.title.includes("password")) errMsg = errMsg + `. ${passwordResetMsg}`
          setError(errMsg);
          setFieldErrors(err.errors);
        } else if (err) {
          let errMsg = err.title;
          if (err.title.includes("password")) errMsg = errMsg + `. ${passwordResetMsg}`
          setError(errMsg);
          setFieldErrors(null);
        } else {
          setError("Login Failed");
          setFieldErrors(null);
        }
      }

      setPassword("");
    }
  };

  const toggleBuyModal = async () => {
    setIsBuyOpen(!isBuyOpen);
  }
  const resetErrors = (field: string) => {
    setError("");
    if (fieldErrors && field) {
      fieldErrors[field] = "";
    }
  };
  var pageType = "";  //Used to identify how RegisterPage will be rendered

  //Custom Components based off build type (Standalone or not)
  let registrationLinks;
  if (!isStandAlone) {
    registrationLinks = (
      <div>
        <p />
        {/* <Link className="link-button" to={{
            pathname: '/register',
          }} state={pageType = "PurchaseSubscription"} >Purchase Subscription</Link>
          <p /> */}
        <Link id="link-button-register-join-organization" className="link-button" to={{
          pathname: '/register',
        }} state={pageType = "JoinOrganization"} >Register with access code</Link>
        <p />
        <div className="copyright">To request an access code, email <a href="mailto:support@auria.space" style={{ color: 'rgba(255, 182, 61)' }}>support@auria.space</a></div>
      </div>
    )
  }
  else {
    registrationLinks = (
      <div>
        <p />
        <Link id="link-button-register-standalone" className="link-button" to={{
          pathname: '/register',
        }} state={pageType = "Standalone"} >Create Account</Link>
      </div>
    )
  }

  return (
    <div className="box-column">
      {!showResetForm ? (
        <>
          <div className="box-column">
            <BorgStyleTextField
              id="textbox-username"
              label="Username"
              value={username}
              autoFocus
              errorText={fieldErrors?.Username && fieldErrors?.Username[0]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                resetErrors("Username");
                setUsername(e.target.value)
              }}
            />
            <BorgStyleTextField
              id="textbox-password"
              label="Password"
              value={password}
              type="password"
              errorText={fieldErrors?.Password && fieldErrors?.Password[0]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                resetErrors("Password");
                setPassword(e.target.value)
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter") {
                  submitLogin();
                }
              }}
            />
            {!fieldErrors && <div className="error">{error}</div>}
              {isPasswordResetAvailable && (
                <div>
                  <button
                    onClick={() => {
                      setShowResetForm(true);
                      onResetModeChange?.(true);
                    }}
                    style={{ background: "none", border: "none", color: "#fcbf49", cursor: "pointer" }}
                  >
                    Forgot password?
                  </button>
                </div>
              )}
          </div>
          <div className="footer">
            <BorgStyleButton id="button-submit" onClick={() => submitLogin()}>Login</BorgStyleButton>
            {registrationLinks}
          </div>
        </>
      ) : (
        <PasswordResetForm
          resetEmail={resetEmail}
          setResetEmail={setResetEmail}
          resetMessage={resetMessage}
          resetError={resetError}
          handlePasswordReset={handlePasswordReset}
          isLoading={isResetLoading}
          onBackToLogin={() => {
            setShowResetForm(false);
            setResetMessage("");
            setResetError("");
            setResetEmail("");
            onResetModeChange?.(false);
          }}
        />
      )}
    </div>
  );
};

export default Login;
